import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "../../assets/logo-icon.png";

const navLinks: { label: string; to: string; children?: { label: string; to: string }[] }[] = [
  { label: "Home", to: "/" },
  {
    label: "About",
    to: "/about",
    children: [
      { label: "About Us", to: "/about" },
      { label: "Our Team", to: "/about/team" },
    ],
  },
  { label: "Our Campaigns", to: "/campaigns" },
  { label: "Our Work", to: "/our-work" },
  // { label: "Impact", to: "/impact" },
  { label: "Resources", to: "/resources" },
  { label: "Partners", to: "/partners" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="bg-primary sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 py-2">
            <img src={logo} alt="CSIP — Community Sanitation Improvement Programme" className="h-10 w-auto bg-white rounded px-2 py-1" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => setAboutOpen(true)}
                  onMouseLeave={() => setAboutOpen(false)}
                >
                  <button
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded transition-colors duration-150 ${
                      location.pathname.startsWith(link.to)
                        ? "text-secondary bg-white/10"
                        : "text-white/80 hover:text-secondary hover:bg-white/10"
                    }`}
                    onClick={() => setAboutOpen((o) => !o)}
                    aria-haspopup="true"
                    aria-expanded={aboutOpen}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
                  </button>

                  {aboutOpen && (
                    <div className="absolute top-full left-0 pt-2 min-w-[180px]">
                      <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2 overflow-hidden">
                        {link.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            end={child.to === "/about"}
                            className={({ isActive }) =>
                              `block px-4 py-2.5 text-sm font-medium transition-colors ${
                                isActive
                                  ? "text-secondary bg-csip-gray"
                                  : "text-primary hover:bg-csip-gray hover:text-secondary"
                              }`
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded transition-colors duration-150 ${
                      isActive
                        ? "text-secondary bg-white/10"
                        : "text-white/80 hover:text-secondary hover:bg-white/10"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          {/* CTA + Mobile toggle */}
          <div className="flex items-center gap-3">
            <Link to="/get-involved" className="btn-primary text-sm hidden sm:inline-block">
              Get Involved
            </Link>
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {menuOpen && (
        <div className="lg:hidden bg-primary-dark border-t border-white/10 px-4 py-3">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.to} className="border-b border-white/10">
                <button
                  className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium ${
                    location.pathname.startsWith(link.to) ? "text-secondary" : "text-white/80"
                  }`}
                  onClick={() => setMobileAboutOpen((o) => !o)}
                  aria-expanded={mobileAboutOpen}
                >
                  {link.label}
                  <ChevronDown size={16} className={`transition-transform ${mobileAboutOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileAboutOpen && (
                  <div className="pb-2 pl-4">
                    {link.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.to === "/about"}
                        onClick={() => {
                          setMenuOpen(false);
                          setMobileAboutOpen(false);
                        }}
                        className={({ isActive }) =>
                          `block px-3 py-2 text-sm ${
                            isActive ? "text-secondary" : "text-white/70 hover:text-secondary"
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-3 text-sm font-medium border-b border-white/10 ${
                    isActive ? "text-secondary" : "text-white/80 hover:text-secondary"
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
          <Link
            to="/get-involved"
            onClick={() => setMenuOpen(false)}
            className="btn-primary text-sm block text-center mt-4"
          >
            Get Involved
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
